import { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  Card,
  SimpleGrid,
  Button,
  Skeleton,
} from "@chakra-ui/react";
import { useAppSelector } from "../../store/hooks";
import {
  FiDownload,
  FiDollarSign,
  FiMapPin,
} from "react-icons/fi";
import { CiMedicalCross } from "react-icons/ci";
import api from "../../lib/api";
import { RegionalDrugConsumption } from "./components/regionalDrugConsumption";
import { InventoryTurnover } from "./components/inventoryTurnover";
import { InferredDiseases } from "./components/inferredDiseases";
import { TopSellingCategories } from "./components/topSellingCategories";
import { toaster } from "../../components/ui/toaster";

interface SalesOverview {
  total_revenue: number;
  total_cost: number;
  total_transactions: number;
  unique_products: number;
  unique_locations: number;
  average_transaction_value: number;
}

export default function DashboardPage() {
  const { user } = useAppSelector((state) => state.auth);
  const [overview, setOverview] = useState<SalesOverview | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(true);

  // States for filters
  const [availableStates, setAvailableStates] = useState<string[]>([]);
  const [selectedState, setSelectedState] = useState<string>("");
  const [dateFilterType, setDateFilterType] = useState<string>("30d");
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");

  // Applied filters (what drives the API calls)
  const [appliedFilters, setAppliedFilters] = useState({
    dateType: "30d",
    startDate: "",
    endDate: "",
    state: "",
  });

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [appliedFilters]);

  const fetchStates = async () => {
    try {
      const response = await api.get("/analytics/states");
      if (response.data?.data) {
        setAvailableStates(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch states", error);
    }
  };

  const calculateDateRange = (type: string, start?: string, end?: string) => {
    const today = new Date();
    const endDateObj = new Date();
    let startDateObj = new Date();

    if (type === "custom" && start && end) {
      return { start_date: start, end_date: end };
    }

    switch (type) {
      case "7d":
        startDateObj.setDate(today.getDate() - 7);
        break;
      case "30d":
        startDateObj.setDate(today.getDate() - 30);
        break;
      case "90d":
        startDateObj.setDate(today.getDate() - 90);
        break;
      case "180d":
        startDateObj.setDate(today.getDate() - 180);
        break;
      case "this-month":
        startDateObj.setDate(1);
        break;
      case "last-month":
        startDateObj.setMonth(today.getMonth() - 1);
        startDateObj.setDate(1);
        endDateObj.setDate(0);
        break;
      default:
        startDateObj.setDate(today.getDate() - 30);
    }

    return {
      start_date: startDateObj.toISOString().split("T")[0],
      end_date: endDateObj.toISOString().split("T")[0],
    };
  };

  const handleApplyFilters = () => {
    setAppliedFilters({
      dateType: dateFilterType,
      startDate: customStartDate,
      endDate: customEndDate,
      state: selectedState,
    });
  };

  const fetchOverview = async () => {
    setLoadingOverview(true);
    try {
      const { start_date, end_date } = calculateDateRange(
        appliedFilters.dateType,
        appliedFilters.startDate,
        appliedFilters.endDate,
      );

      let url = `/analytics/sales/overview?start_date=${start_date}&end_date=${end_date}`;
      if (appliedFilters.state) {
        url += `&state=${encodeURIComponent(appliedFilters.state)}`;
      }

      const response = await api.get(url);
      setOverview(response.data.data || response.data);
    } catch (error: any) {
      console.error("Failed to fetch overview:", error);
      toaster.create({
        title: "Error",
        description: "Failed to load dashboard metrics",
        type: "error",
      });
    } finally {
      setLoadingOverview(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat("en-NG").format(value);
  };

  return (
    <VStack gap={6} align="stretch" p={{ base: 4, md: 6 }}>
      {/* Header */}
      <Box>
        <Heading size="xl">Welcome, {user?.first_name}!</Heading>
        <Text color="gray.600" mt={1}>
          Your pharmacy management dashboard
        </Text>
      </Box>

      {/* Filters */}
      <Card.Root>
        <Card.Body>
          <VStack gap={4} align="stretch">
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={3}>
              {/* State Filter */}
              <Box>
                <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={2}>
                  State
                </Text>
                <select
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm bg-white"
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}>
                  <option value="">All States</option>
                  {availableStates.map((st) => (
                    <option key={st} value={st}>
                      {st}
                    </option>
                  ))}
                </select>
              </Box>

              {/* Date Range */}
              <Box>
                <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={2}>
                  Date Range
                </Text>
                <select
                  className="w-full px-3 py-2 border border-gray-200 rounded text-sm bg-white"
                  value={dateFilterType}
                  onChange={(e) => setDateFilterType(e.target.value)}>
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                  <option value="180d">Last 180 days</option>
                  <option value="this-month">This Month</option>
                  <option value="last-month">Last Month</option>
                  <option value="custom">Custom Range</option>
                </select>
              </Box>

              {/* Custom Start Date */}
              {dateFilterType === "custom" && (
                <>
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color="gray.600"
                      mb={2}>
                      From
                    </Text>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                    />
                  </Box>

                  {/* Custom End Date */}
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color="gray.600"
                      mb={2}>
                      To
                    </Text>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                    />
                  </Box>
                </>
              )}
            </SimpleGrid>

            {/* Action Buttons */}
            <Box display="flex" justifyContent="flex-end" gap={2} pt={2}>
              <Button size="sm" colorScheme="blue" onClick={handleApplyFilters}>
                Apply Filters
              </Button>
              <Button variant="outline" size="sm">
                <FiDownload />
                <Text ml={2}>Export</Text>
              </Button>
            </Box>
          </VStack>
        </Card.Body>
      </Card.Root>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={4}>
        {/* Total Revenue Card */}
        <Card.Root>
          <Card.Body>
            <div className="flex items-center justify-between">
              <p className="text-gray-600 font-medium text-sm">Total Revenue</p>
              <div className="w-8 h-8 flex items-center justify-center bg-green-50 rounded-md">
                <FiDollarSign color="#10b981" size={18} />
              </div>
            </div>
          </Card.Body>
          <Card.Footer>
            {loadingOverview ? (
              <Skeleton height="32px" width="120px" />
            ) : (
              <h5 className="font-bold text-2xl text-black">
                {overview ? formatCurrency(overview.total_revenue) : "₦0"}
              </h5>
            )}
          </Card.Footer>
        </Card.Root>

        {/* Total Transactions Card */}
        <Card.Root>
          <Card.Body>
            <div className="flex items-center justify-between">
              <p className="text-gray-600 font-medium text-sm">Transactions</p>
              <div className="w-8 h-8 flex items-center justify-center bg-purple-50 rounded-md">
                <CiMedicalCross color="#8b5cf6" size={18} />
              </div>
            </div>
          </Card.Body>
          <Card.Footer>
            {loadingOverview ? (
              <Skeleton height="32px" width="80px" />
            ) : (
              <h5 className="font-bold text-2xl text-black">
                {overview ? formatNumber(overview.total_transactions) : "0"}
              </h5>
            )}
          </Card.Footer>
        </Card.Root>

        {/* Unique Locations Card */}
        <Card.Root>
          <Card.Body>
            <div className="flex items-center justify-between">
              <p className="text-gray-600 font-medium text-sm">Locations</p>
              <div className="w-8 h-8 flex items-center justify-center bg-orange-50 rounded-md">
                <FiMapPin color="#f97316" size={18} />
              </div>
            </div>
          </Card.Body>
          <Card.Footer>
            {loadingOverview ? (
              <Skeleton height="32px" width="60px" />
            ) : (
              <h5 className="font-bold text-2xl text-black">
                {overview ? formatNumber(overview.unique_locations) : "0"}
              </h5>
            )}
          </Card.Footer>
        </Card.Root>
      </SimpleGrid>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Pass entire appliedFilters object to children or spread props as needed. 
            However, existing children expect 'dateFilter' (string). 
            We might need to update Children or pass calculated dates. 
            For now, let's keep 'dateFilter' string logic but we'll need to enhance children 
            to support 'custom' + start/end dates OR state filters.
            
            Ideally, we should refactor children to accept { startDate, endDate, state }.
            Since that's a larger refactor, for this step, if the user picks 'custom', 
            passing 'custom' as string to children won't work unless children are updated.
        */}
        <RegionalDrugConsumption
          dateFilter={appliedFilters.dateType}
          customRange={{
            start: appliedFilters.startDate,
            end: appliedFilters.endDate,
          }}
          stateFilter={appliedFilters.state}
        />
        <InventoryTurnover
          dateFilter={appliedFilters.dateType}
          customRange={{
            start: appliedFilters.startDate,
            end: appliedFilters.endDate,
          }}
          stateFilter={appliedFilters.state}
        />
        <InferredDiseases
          dateFilter={appliedFilters.dateType}
          customRange={{
            start: appliedFilters.startDate,
            end: appliedFilters.endDate,
          }}
          stateFilter={appliedFilters.state}
        />
        <TopSellingCategories
          dateFilter={appliedFilters.dateType}
          customRange={{
            start: appliedFilters.startDate,
            end: appliedFilters.endDate,
          }}
          stateFilter={appliedFilters.state}
        />
      </div>
    </VStack>
  );
}
